// src/ConsumerApp/Screens/ProductsList/components/DesktopFiltersSidebar.js

import React, { useState, useMemo } from 'react';
import { theme } from '../../../../assets/theme/theme';

export default function DesktopFiltersSidebar({
  filterConfig = [],
  appliedFilters = [],
  onToggleFilter,
  onClearAll,
}) {
  const [searchQueries, setSearchQueries] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleSearchChange = (groupId, val) => {
    setSearchQueries((prev) => ({
      ...prev,
      [groupId]: val,
    }));
  };

  if (!filterConfig || filterConfig.length === 0) {
    return null;
  }

  return (
    <aside style={styles.sidebarContainer}>
      {/* Sidebar Header */}
      <div style={styles.sidebarHeader}>
        <div style={styles.headerTitleWrap}>
          <span style={styles.filterMainTitle}>Filters</span>
          {appliedFilters.length > 0 && (
            <span style={styles.activeTotalBadge}>{appliedFilters.length}</span>
          )}
        </div>
        {appliedFilters.length > 0 && (
          <button type="button" style={styles.clearBtn} onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>

      {/* Accordion List */}
      <div style={styles.groupsStack}>
        {filterConfig.map((category) => {
          const isCollapsed = Boolean(collapsedGroups[category.id]);
          const query = (searchQueries[category.id] || '').toLowerCase();
          const filteredOptions = (category.options || []).filter((opt) =>
            opt.label.toLowerCase().includes(query)
          );

          const activeCountInCategory = appliedFilters.filter(
            (f) => f.categoryId === category.id
          ).length;

          return (
            <div key={category.id} style={styles.filterGroup}>
              {/* Group Header */}
              <div
                style={styles.groupHeader}
                onClick={() => toggleGroupCollapse(category.id)}
              >
                <div style={styles.groupTitleWrap}>
                  <span style={styles.groupLabel}>{category.label}</span>
                  {activeCountInCategory > 0 && (
                    <span style={styles.categoryCountBadge}>
                      {activeCountInCategory}
                    </span>
                  )}
                </div>
                <span style={styles.collapseIcon}>
                  {isCollapsed ? '+' : '−'}
                </span>
              </div>

              {/* Group Body */}
              {!isCollapsed && (
                <div style={styles.groupBody}>
                  {/* Search input if category has more than 5 options */}
                  {category.options.length > 5 && (
                    <div style={styles.searchBoxWrap}>
                      <input
                        type="text"
                        placeholder={`Search ${category.label.toLowerCase()}...`}
                        value={searchQueries[category.id] || ''}
                        onChange={(e) =>
                          handleSearchChange(category.id, e.target.value)
                        }
                        style={styles.groupSearchInput}
                      />
                    </div>
                  )}

                  {/* Options Checkbox List */}
                  <div style={styles.optionsList}>
                    {filteredOptions.length === 0 ? (
                      <span style={styles.noResultsText}>No matches found</span>
                    ) : (
                      filteredOptions.map((option) => {
                        const isChecked = appliedFilters.some(
                          (f) => f.optionId === option.id
                        );

                        return (
                          <label key={option.id} style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => onToggleFilter(category, option)}
                              style={styles.realCheckbox}
                            />
                            <span
                              style={{
                                ...styles.customBox,
                                ...(isChecked ? styles.customBoxChecked : {}),
                              }}
                            >
                              {isChecked && (
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#FFFFFF"
                                  strokeWidth="3.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </span>
                            <span
                              style={{
                                ...styles.optionText,
                                ...(isChecked ? styles.optionTextActive : {}),
                              }}
                            >
                              {option.label}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

const styles = {
  sidebarContainer: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #E2E8F0',
    padding: '16px 18px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 92, // Sits neatly below fixed navigation bar
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 14,
    borderBottom: '1px solid #F1F5F9',
  },
  headerTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  filterMainTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: '#2B2A29',
  },
  activeTotalBadge: {
    fontSize: 11,
    fontWeight: '700',
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    padding: '1px 6px',
    borderRadius: 999,
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
    cursor: 'pointer',
    padding: 0,
  },
  groupsStack: {
    display: 'flex',
    flexDirection: 'column',
  },
  filterGroup: {
    borderBottom: '1px solid #F1F5F9',
    padding: '12px 0',
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    padding: '4px 0',
  },
  groupTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  groupLabel: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  categoryCountBadge: {
    fontSize: 10.5,
    fontWeight: '700',
    color: '#E9001E',
    backgroundColor: '#FFF1F2',
    padding: '1px 5px',
    borderRadius: 4,
  },
  collapseIcon: {
    fontSize: 16,
    fontWeight: '700',
    color: '#707B8C',
    lineHeight: 1,
  },
  groupBody: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  searchBoxWrap: {
    marginBottom: 4,
  },
  groupSearchInput: {
    width: '100%',
    height: 32,
    borderRadius: 6,
    border: '1px solid #CBD5E1',
    padding: '0 8px',
    fontSize: 12,
    color: '#2B2A29',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#F8FAFC',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 190,
    overflowY: 'auto',
    paddingRight: 4,
  },
  noResultsText: {
    fontSize: 12,
    color: '#94A3B8',
    fontStyle: 'italic',
    padding: '4px 0',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    userSelect: 'none',
  },
  realCheckbox: {
    display: 'none',
  },
  customBox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    border: '1.5px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },
  customBoxChecked: {
    borderColor: '#E9001E',
    backgroundColor: '#E9001E',
  },
  optionText: {
    fontSize: 13,
    color: '#475569',
    lineHeight: '17px',
  },
  optionTextActive: {
    color: '#2B2A29',
    fontWeight: '600',
  },
};